import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, UserRound } from "lucide-react";
import { ChatLeadForm } from "./ChatLeadForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SID_KEY = "dar_chat_session";
const WELCOME = {
  role: "assistant",
  content:
    "Buongiorno, sono l'assistente dello Studio Legale D'Arienzo. Posso aiutarti a capire se lo studio si occupa della tua questione e come prenotare una consulenza. Di cosa hai bisogno?",
};
const SUGGESTIONS = [
  "Ho ricevuto una cartella dall'Agenzia delle Entrate",
  "Devo recuperare un credito",
  "Mi serve un Modello 231 per l'azienda",
  "Come prenoto un appuntamento?",
];

async function streamChat(body, onEvent) {
  const res = await fetch(`${API}/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    parts.forEach((p) => p.startsWith("data: ") && onEvent(JSON.parse(p.slice(6))));
  }
}

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div
      className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${
        m.role === "user" ? "bg-navy-900 text-white rounded-br-md" : "bg-navy-50 text-navy-900 rounded-bl-md"
      }`}
    >
      {m.content || <span className="inline-flex gap-1"><span className="h-1.5 w-1.5 rounded-full bg-navy-500 animate-bounce" /><span className="h-1.5 w-1.5 rounded-full bg-navy-500 animate-bounce [animation-delay:150ms]" /><span className="h-1.5 w-1.5 rounded-full bg-navy-500 animate-bounce [animation-delay:300ms]" /></span>}
    </div>
  </div>
);

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sid, setSid] = useState(() => localStorage.getItem(SID_KEY));
  const [showLead, setShowLead] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, showLead]);

  const send = async (text) => {
    const msg = text.trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    const append = (t) => setMessages((m) => [...m.slice(0, -1), { role: "assistant", content: m[m.length - 1].content + t }]);
    try {
      await streamChat({ session_id: sid, message: msg }, (ev) => {
        if (ev.type === "session") { setSid(ev.session_id); localStorage.setItem(SID_KEY, ev.session_id); }
        if (ev.type === "delta" || ev.type === "error") append(ev.content);
      });
    } catch {
      append("Al momento non riesco a rispondere. Puoi chiamare lo studio al +39 0773 1647297.");
    } finally {
      setBusy(false);
    }
  };

  const leadDone = (name) => {
    setShowLead(false);
    setMessages((m) => [...m, { role: "assistant", content: `Grazie ${name}, ho inviato i tuoi contatti allo studio. Sarai ricontattato al più presto.` }]);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="chat-toggle-button"
        aria-label={open ? "Chiudi chat" : "Apri chat"}
        className="fixed bottom-5 right-5 z-50 flex h-14 items-center gap-2 rounded-full bg-bronze pl-4 pr-5 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(0,43,74,0.35)] hover:bg-bronze-600 transition-colors"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
        <span className="hidden sm:inline">{open ? "Chiudi" : "Chiedi allo studio"}</span>
      </button>
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed z-50 bottom-24 right-3 left-3 sm:left-auto sm:right-5 sm:w-[400px] h-[min(620px,calc(100vh-8rem))] flex flex-col overflow-hidden rounded-3xl bg-white border border-navy-100 shadow-[0_30px_60px_rgba(0,43,74,0.25)] animate-rise"
        >
          <div className="flex items-center gap-3 bg-navy-900 px-5 py-4">
            <img src="/brand/monogram-white.png" alt="" className="h-9 w-9 object-contain" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-white">Assistente dello Studio</p>
              <p className="text-[11px] text-navy-200">Informazioni generali, non pareri legali</p>
            </div>
            <button type="button" onClick={() => setOpen(false)} data-testid="chat-close-button" aria-label="Chiudi" className="text-white/80 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-5 space-y-3">
            {messages.map((m, i) => <Bubble key={i} m={m} />)}
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((s, i) => (
                  <button key={s} type="button" onClick={() => send(s)} data-testid={`chat-suggestion-${i}`} className="rounded-full border border-navy-200 px-3 py-1.5 text-xs text-navy-900 hover:border-navy-900 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            )}
            {showLead && sid && <ChatLeadForm sessionId={sid} onDone={leadDone} onCancel={() => setShowLead(false)} />}
            <div ref={endRef} />
          </div>
          {sid && !showLead && (
            <button type="button" onClick={() => setShowLead(true)} data-testid="chat-lead-open" className="mx-4 mb-2 inline-flex items-center justify-center gap-2 rounded-full border border-bronze px-4 py-2 text-xs font-semibold text-bronze-600 hover:bg-bronze hover:text-white transition-colors">
              <UserRound className="h-4 w-4" /> Lascia i tuoi contatti
            </button>
          )}
          <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex items-center gap-2 border-t border-navy-100 p-3">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              maxLength={1000}
              placeholder="Scrivi la tua domanda..."
              data-testid="chat-input"
              className="flex-1 h-11 rounded-full bg-navy-50 px-4 text-sm text-navy-900 outline-none focus:ring-2 focus:ring-navy-900"
            />
            <button type="submit" disabled={busy || !input.trim()} data-testid="chat-send-button" aria-label="Invia" className="flex h-11 w-11 items-center justify-center rounded-full bg-navy-900 text-white disabled:opacity-40 hover:bg-navy-700 transition-colors">
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
